/**
 * Centralized Schedule visuals: Tailwind color/border tokens, composed day-column rules,
 * locked-day surfaces, and default ScopeBar paint.
 *
 * Layout numbers (widths, zoom order, bar insets) live in constants.js.
 */

export const BG_PRIMARY_0 = "bg-gray-50";
export const BG_PRIMARY_1 = "bg-[#f1f1f1]";
export const BG_PRIMARY_1_70 = "bg-gray-200/70";
export const BG_PRIMARY_2 = "bg-gray-200";
export const BG_PRIMARY_3 = "bg-gray-200/50";
export const BG_PRIMARY_4 = "bg-gray-400/40";
export const BG_SURFACE = "bg-white";
export const HOVER_BG_PRIMARY_0 = "hover:bg-gray-50";
export const HOVER_BG_PRIMARY_1 = "hover:bg-gray-100";
export const HOVER_BG_PRIMARY_2 = "hover:bg-gray-200";

export const BORDER_PRIMARY_0 = "border-gray-50";
export const BORDER_PRIMARY_1 = "border-gray-200/50";
export const BORDER_PRIMARY_2 = "border-gray-200";
export const BORDER_PRIMARY_3 = "border-gray-300";
export const BORDER_PRIMARY_4 = "border-gray-500";

export const BORDER_SURFACE = "border-white";

export const BORDER_THICKNESS_THIN = 1;
export const BORDER_THICKNESS_STANDARD = 2;
export const BORDER_THICKNESS_THICK = 3;

// export const LOCKED_DAY_BG_PRIMARY = "bg-green-900/15";
// export const LOCKED_DAY_BG_SECONDARY = "bg-green-950/20";
export const LOCKED_DAY_BG_PRIMARY = "";
export const LOCKED_DAY_BG_SECONDARY = "";

/**
 * Locked-day visuals when Dispatch `DayDetails.locked` is true for that calendar date.
 * Tailwind background utilities — adjust here to retheme header, grid, and ScopeBar together.
 */
export const SCHEDULE_TIMELINE_LOCKED_DAY = Object.freeze({
  /** Header strip + grid cell tint under row striping. */
  surfaceBgClass: LOCKED_DAY_BG_PRIMARY,
  /** Inner layer on ScopeBar per-column shields (wrapper uses `opacity-60`). */
  scopeBarShieldBgClass: LOCKED_DAY_BG_SECONDARY,
});

/**
 * Default look for timeline bar chips (paint + typography; geometry is SCHEDULE_BAR in constants).
 */
export const SCHEDULE_BAR_STYLE = Object.freeze({
  /** Used when block has no group color (worker-based coloring TBD). */
  defaultColor: "#2563EB",
  textColor: "#FFFFFF",
  fontSizePx: 11,
  /** CSS font-weight (Tailwind `font-medium` ≈ 500). */
  fontWeight: 500,
  borderRadiusPx: 4,
  /** Approximates Tailwind `shadow-sm`. */
  boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  zIndex: 20,
});

/**
 * Crew override range strip on ScopeBar ({@link OverrideStrip}): thin bar + draggable resize handles.
 */
export const SCHEDULE_WORKER_OVERRIDE_STRIP = Object.freeze({
  /** Thin horizontal indicator over contiguous override days. */
  barBgClass: "bg-primary",
  /** Resize handles at strip ends (pointer draggable). */
  handleBgClass: "bg-primary/90",
  handleHoverBgClass: "hover:bg-primary",
});

/**
 * Schedule semantic styling map.
 *
 * Components should consume these role-based constants instead of raw palette tokens
 * so schedule-wide visual changes stay centralized here.
 */
export const SCHEDULE_GROUP_CELL_BG = BG_PRIMARY_0;
export const SCHEDULE_PROJECT_CELL_BG = BG_PRIMARY_1;
export const SCHEDULE_GROUP_WEEKEND_BG = BG_PRIMARY_1;
export const SCHEDULE_PROJECT_WEEKEND_BG = BG_PRIMARY_1_70;
export const SCHEDULE_OUT_OF_JOB_RANGE_BG = BG_PRIMARY_2;
export const SCHEDULE_PLACEHOLDER_CELL_BG = BG_SURFACE;
export const SCHEDULE_HEADER_CELL_BG = BG_PRIMARY_3;

export const SCHEDULE_GROUP_CELL_BORDER = BORDER_PRIMARY_1;
export const SCHEDULE_GROUP_CELL_BORDER_THICKNESS = BORDER_THICKNESS_THIN;
export const SCHEDULE_PROJECT_CELL_BORDER = BORDER_PRIMARY_1;
export const SCHEDULE_PROJECT_CELL_BORDER_THICKNESS = BORDER_THICKNESS_THIN;
export const SCHEDULE_PROJECT_CELL_TOP_BORDER_THICKNESS = BORDER_THICKNESS_STANDARD;

/** Project header row: thick top divider color (see {@link SCHEDULE_PROJECT_CELL_BORDER} for thin right edge). */
export const SCHEDULE_PROJECT_CELL_TOP_BORDER = BORDER_PRIMARY_3;
export const SCHEDULE_HEADER_CELL_BORDER = BORDER_PRIMARY_3;
export const SCHEDULE_HEADER_CELL_BORDER_THICKNESS = BORDER_THICKNESS_THIN;
export const SCHEDULE_HEADER_PANEL_DIVIDER_BORDER_THICKNESS = BORDER_THICKNESS_STANDARD;

/** Canvas seam color after Fri→Mon hidden-weekend gap — thick **right** edge on Friday’s column. */
export const SCHEDULE_HIDDEN_WEEKEND_GAP_BORDER = BORDER_PRIMARY_3;
export const SCHEDULE_HIDDEN_WEEKEND_GAP_BORDER_THICKNESS = BORDER_THICKNESS_STANDARD;
